import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'
import { ArrowRight, Shield } from 'lucide-react'

// ─── Tokens — half-canvas: brand on the left, Claude-cream on the right ────
const T = {
  // Left (dark) panel — 99 Dash brand
  ink: '#0A0A14',
  inkElev: '#13131c',
  inkText: '#E6E6EA',
  inkDim: 'rgba(230,230,234,0.55)',
  inkHairline: 'rgba(230,230,234,0.10)',
  amber: '#F59E0B',

  // Right (cream) panel — Claude-style
  cream: '#FAF9F5',          // warm off-white, not stark white
  creamElev: '#FFFFFF',
  charcoal: '#191919',       // primary text
  charcoalDim: '#6B6B6B',    // secondary text
  hair: '#E8E5DD',           // hairline borders, warm gray
  hairFocus: '#1f1f1f',      // input focus border
  errorBg: '#FEF1EE',
  errorBorder: '#E6A89A',
  errorInk: '#9A2E1A',
}

// ─── Variant B (Counter Dot) — matches favicon.svg + sidebar ────────────────
function LogoMark({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <rect width="32" height="32" rx="6" fill={T.ink} />
      <g stroke={T.inkText} strokeWidth="2" strokeLinecap="square" fill="none">
        <circle cx="10" cy="11" r="4" />
        <path d="M 14 11 L 14 22" />
        <circle cx="22" cy="11" r="4" />
        <path d="M 26 11 L 26 22" />
      </g>
      <circle cx="22" cy="11" r="1.6" fill={T.amber} />
    </svg>
  )
}

// Mono stack — keeps brand voice on the dark side without re-adding JetBrains
const MONO = 'ui-monospace, SFMono-Regular, "Cascadia Code", Menlo, monospace'
// Serif stack — Claude's warmth on the cream side without licensing Tiempos
const SERIF = '"Tiempos Headline", "Charter", "Iowan Old Style", "Sitka Heading", Georgia, serif'

export function Login() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // MFA Challenge state
  const [showMfaChallenge, setShowMfaChallenge] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaUserId, setMfaUserId] = useState(null)
  const [tempSession, setTempSession] = useState(null)
  const [useBackupCode, setUseBackupCode] = useState(false)

  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await api.login(email, password)
      if (data.requiresMfa) {
        setMfaUserId(data.userId)
        setTempSession(data.tempSession)
        setShowMfaChallenge(true)
        setLoading(false)
        return
      }
      api.setToken(data.session.access_token)
      useAuthStore.setState({
        user: data.user,
        buyer: data.buyer,
        isAuthenticated: true,
      })
      useAuthStore.getState().fetchPermissions()
      navigate(data.user.isAdmin ? '/admin' : '/dashboard')
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaChallenge = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await api.mfaChallenge(mfaUserId, mfaCode, useBackupCode)
      api.setToken(tempSession.access_token)
      useAuthStore.setState({
        user: data.user,
        buyer: data.buyer,
        isAuthenticated: true,
      })
      useAuthStore.getState().fetchPermissions()
      navigate(data.user.isAdmin ? '/admin' : '/dashboard')
    } catch (err) {
      setError(err.message || 'Invalid code')
      setMfaCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex antialiased" style={{ background: T.cream }}>
      {/* ─── Left half — dark brand panel (≥lg only) ───────────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-14"
        style={{ background: T.ink, color: T.inkText }}
      >
        {/* Top: logo + wordmark */}
        <div className="flex items-center gap-4 animate-fade-in">
          <LogoMark size={44} />
          <div className="flex flex-col" style={{ gap: 3 }}>
            <span style={{
              fontFamily: MONO,
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '0.34em',
              color: T.inkText,
              textTransform: 'uppercase',
            }}>
              99 Dash
            </span>
            <span style={{
              fontFamily: MONO,
              fontWeight: 500,
              fontSize: 9.5,
              letterSpacing: '0.36em',
              color: T.amber,
              textTransform: 'uppercase',
            }}>
              Sovereign Analyst
            </span>
          </div>
        </div>

        {/* Center: editorial-style line + session clock */}
        <div className="space-y-10 animate-fade-in stagger-2">
          <p style={{
            fontFamily: SERIF,
            fontSize: 30,
            lineHeight: 1.25,
            fontWeight: 400,
            color: T.inkText,
            maxWidth: 460,
            letterSpacing: '-0.01em',
          }}>
            Marketing capital, deployed with intent.
            <span style={{ color: T.inkDim }}> Live attribution from click to call,
            across every market.</span>
          </p>

          {/* Hairline */}
          <div style={{ borderTop: `1px solid ${T.inkHairline}` }} />

          {/* Quiet session info */}
          <div className="flex items-baseline gap-12">
            <div>
              <div style={{
                fontFamily: MONO,
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.34em',
                color: T.inkDim,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Paris
              </div>
              <div style={{
                fontFamily: MONO,
                fontSize: 32,
                fontWeight: 600,
                letterSpacing: '-0.5px',
                color: T.inkText,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {now.toLocaleTimeString('en-GB', {
                  hour: '2-digit', minute: '2-digit',
                  timeZone: 'Europe/Paris',
                })}
              </div>
            </div>
            <div>
              <div style={{
                fontFamily: MONO,
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.34em',
                color: T.inkDim,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Today
              </div>
              <div style={{
                fontFamily: MONO,
                fontSize: 14,
                color: T.inkText,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {now.toLocaleDateString('en-US', {
                  weekday: 'long', month: 'short', day: 'numeric',
                  timeZone: 'Europe/Paris',
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: minimal footer */}
        <div className="animate-fade-in stagger-4" style={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '0.34em',
          color: T.inkDim,
          textTransform: 'uppercase',
        }}>
          © 99medias · Internal
        </div>
      </div>

      {/* ─── Right half — cream form panel ─────────────────────────────────── */}
      <div
        className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: T.cream, color: T.charcoal }}
      >
        <div className="w-full max-w-[400px] animate-fade-in stagger-1">
          {/* Mobile-only logo (left panel hidden on small screens) */}
          <div className="lg:hidden flex items-center gap-3 mb-12 justify-center">
            <LogoMark size={36} />
            <span style={{
              fontFamily: MONO,
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '0.34em',
              color: T.charcoal,
              textTransform: 'uppercase',
            }}>
              99 Dash
            </span>
          </div>

          {/* Heading — serif, generous space (Claude pattern) */}
          <div className="mb-10">
            <h1 style={{
              fontFamily: SERIF,
              fontSize: 34,
              fontWeight: 400,
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
              color: T.charcoal,
            }}>
              {showMfaChallenge ? 'Verify it’s you' : 'Welcome back'}
            </h1>
            <p style={{
              marginTop: 10,
              fontSize: 15,
              lineHeight: 1.5,
              color: T.charcoalDim,
            }}>
              {showMfaChallenge
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Sign in with your 99medias credentials to continue.'}
            </p>
          </div>

          {!showMfaChallenge ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {error && <ErrorBanner message={error} />}

              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@99medias.com"
                  required
                  autoFocus
                />
              </Field>

              <Field label="Password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </Field>

              <PrimaryButton
                type="submit"
                disabled={!email || !password || loading}
                loading={loading}
                label="Continue"
              />
            </form>
          ) : (
            <form onSubmit={handleMfaChallenge} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {error && <ErrorBanner message={error} />}

              <div style={{
                padding: '12px 14px',
                background: T.creamElev,
                border: `1px solid ${T.hair}`,
                borderRadius: 8,
                fontSize: 13.5,
                lineHeight: 1.5,
                color: T.charcoal,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <Shield className="w-4 h-4 flex-shrink-0" style={{ color: T.amber, marginTop: 2 }} />
                <span>
                  {useBackupCode
                    ? 'Enter one of your backup codes.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </span>
              </div>

              <Field label={useBackupCode ? 'Backup code' : 'Authentication code'}>
                <Input
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, useBackupCode ? 10 : 6))}
                  placeholder={useBackupCode ? 'XXXXXXXXXX' : '000000'}
                  required
                  autoFocus
                  style={{
                    textAlign: 'center',
                    fontSize: 22,
                    letterSpacing: '0.5em',
                    fontFamily: MONO,
                    paddingTop: 14,
                    paddingBottom: 14,
                  }}
                />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    setUseBackupCode(!useBackupCode)
                    setMfaCode('')
                    setError('')
                  }}
                  style={{
                    fontSize: 13.5,
                    color: T.charcoal,
                    fontWeight: 500,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                  }}
                >
                  {useBackupCode ? 'Use authenticator code' : 'Use backup code instead'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <SecondaryButton
                  onClick={() => {
                    setShowMfaChallenge(false)
                    setMfaCode('')
                    setError('')
                    setUseBackupCode(false)
                  }}
                >
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!mfaCode || (useBackupCode ? mfaCode.length !== 10 : mfaCode.length !== 6) || loading}
                  loading={loading}
                  label="Verify"
                />
              </div>
            </form>
          )}

          {/* Quiet legal at bottom */}
          {!showMfaChallenge && (
            <p style={{
              marginTop: 32,
              fontSize: 12,
              color: T.charcoalDim,
              lineHeight: 1.5,
            }}>
              Restricted access. By continuing, you agree to the 99medias internal use policy.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 12.5,
        fontWeight: 500,
        color: T.charcoal,
        marginBottom: 6,
      }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function Input({ style: extraStyle = {}, ...props }) {
  const baseStyle = {
    width: '100%',
    padding: '11px 14px',
    fontSize: 14.5,
    background: T.creamElev,
    border: `1px solid ${T.hair}`,
    borderRadius: 8,
    color: T.charcoal,
    outline: 'none',
    transition: 'border-color 150ms, box-shadow 150ms',
    fontFamily: 'inherit',
  }
  return (
    <input
      {...props}
      style={{ ...baseStyle, ...extraStyle }}
      onFocus={(e) => {
        e.target.style.borderColor = T.charcoal
        e.target.style.boxShadow = `0 0 0 3px rgba(25,25,25,0.08)`
      }}
      onBlur={(e) => {
        e.target.style.borderColor = T.hair
        e.target.style.boxShadow = 'none'
      }}
    />
  )
}

function PrimaryButton({ children, label, loading, disabled, type = 'button', onClick }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        width: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '13px 18px',
        background: disabled ? '#D6D2C8' : T.charcoal,
        color: disabled ? '#8a8a8a' : T.cream,
        fontSize: 14.5,
        fontWeight: 500,
        borderRadius: 8,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms, transform 80ms',
        fontFamily: 'inherit',
      }}
      onMouseDown={(e) => !disabled && (e.currentTarget.style.transform = 'scale(0.99)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
    >
      {loading ? (
        <>
          <span
            className="animate-spin"
            style={{
              display: 'inline-block',
              width: 14, height: 14,
              border: `2px solid rgba(250,249,245,0.3)`,
              borderTopColor: T.cream,
              borderRadius: '50%',
            }}
          />
          <span>One moment</span>
        </>
      ) : (
        <>
          <span>{label || children}</span>
          <ArrowRight size={15} />
        </>
      )}
    </button>
  )
}

function SecondaryButton({ children, onClick, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      style={{
        flex: 1,
        padding: '13px 18px',
        background: 'transparent',
        border: `1px solid ${T.hair}`,
        color: T.charcoal,
        fontSize: 14.5,
        fontWeight: 500,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'border-color 150ms, background 150ms',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = T.charcoal
        e.currentTarget.style.background = T.creamElev
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = T.hair
        e.currentTarget.style.background = 'transparent'
      }}
    >
      {children}
    </button>
  )
}

function ErrorBanner({ message }) {
  return (
    <div style={{
      padding: '11px 14px',
      background: T.errorBg,
      border: `1px solid ${T.errorBorder}`,
      borderRadius: 8,
      fontSize: 13.5,
      color: T.errorInk,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
    }}>
      <span style={{ marginTop: 1, flexShrink: 0 }}>·</span>
      <span>{message}</span>
    </div>
  )
}
